import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";

// ── Theme hook ────────────────────────────────────────────────────
function useTheme() {
  const [theme, setTheme] = useState({ primary: "#006080", dark: "#004d66", name: "ExcelToAPI" });
  useEffect(() => {
    client.get("/admin/theme").then(r => setTheme({
      primary: r.data.primary_color || "#006080",
      dark: r.data.primary_dark || "#004d66",
      name: r.data.app_name || "ExcelToAPI",
    })).catch(() => {});
  }, []);
  return theme;
}

// ── Typing animation ──────────────────────────────────────────────
const BENEFITS = [
  "Because your spreadsheet deserves a better life.",
  "Turn 50,000 rows into 50,000 API calls. Unattended.",
  "Your CSV rang. It wants to be productive.",
  "No code. No problem. Bring your Excel file.",
  "From inbox attachment to API result in 5 minutes.",
  "VLOOKUP was never meant to call REST APIs. We were.",
  "Process data while you sleep. (The coffee is optional.)",
  "One template. Infinite reuse. Your future self says thanks.",
  "Enriched data pipelines. Zero Python required.",
  "Email arrives → magic happens → results fly back.",
];

function TypeWriter({ color }) {
  const [text, setText] = useState("");
  const [msgIdx, setMsgIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [pause, setPause] = useState(false);

  useEffect(() => {
    if (pause) return;
    const msg = BENEFITS[msgIdx];
    const speed = deleting ? 30 : 60;
    const timer = setTimeout(() => {
      if (!deleting) {
        if (charIdx < msg.length) { setText(msg.slice(0, charIdx + 1)); setCharIdx(c => c + 1); }
        else { setPause(true); setTimeout(() => { setDeleting(true); setPause(false); }, 2200); }
      } else {
        if (charIdx > 0) { setText(msg.slice(0, charIdx - 1)); setCharIdx(c => c - 1); }
        else { setDeleting(false); setMsgIdx(i => (i + 1) % BENEFITS.length); }
      }
    }, speed);
    return () => clearTimeout(timer);
  }, [charIdx, deleting, msgIdx, pause]);

  return (
    <span style={{ color }}>
      {text}
      <span className="inline-block w-0.5 h-[1em] ml-0.5 align-middle animate-pulse" style={{ background: color }} />
    </span>
  );
}

// ── Floating particles background ────────────────────────────────
function ParticlesBg({ color }) {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 3 + 1,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      o: Math.random() * 0.4 + 0.05,
    }));

    let raf;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `${color}${Math.floor(p.o * 255).toString(16).padStart(2, "0")}`;
        ctx.fill();
      });
      // Draw connecting lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const d = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (d < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `${color}${Math.floor((1 - d / 120) * 0.15 * 255).toString(16).padStart(2, "0")}`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [color]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />;
}

// ── Feature cards ─────────────────────────────────────────────────
const FEATURES = [
  { icon: "📊", title: "File Upload & Parsing", desc: "Excel, CSV, XLS up to 50MB. Auto-detects headers, column types, and delimiters.", flip: "Your spreadsheet arrived first class. We rolled out the red carpet." },
  { icon: "🔀", title: "Transformation Pipeline", desc: "11 step types: rename, type convert, date format, string ops, calculated fields, lookups.", flip: "Like a personal trainer for your data. Brutal, effective, no complaints." },
  { icon: "🔍", title: "Lookup Tables", desc: "Key→Value and Numeric Range lookups with wildcard * fallback and fuzzy matching.", flip: "VLOOKUP wished it was this cool. It wasn't. It still isn't." },
  { icon: "🌐", title: "Visual API Builder", desc: "Define REST APIs with all auth types, headers, JSON templates using {{fieldname}} refs.", flip: "Postman is jealous. We don't blame it." },
  { icon: "⚡", title: "Parallel Execution", desc: "Configurable concurrency, rate-limit retry, real-time SSE logs, pause/resume/stop.", flip: "50,000 rows? Hold my coffee." },
  { icon: "📬", title: "Email Trigger (IMAP)", desc: "Monitor mailboxes. Attachment arrives → process → send results back automatically.", flip: "You emailed it, we handled it, you checked your phone. Perfect." },
  { icon: "🔗", title: "Execution Sequences", desc: "Chain multiple APIs. Each step enriches data with resp_* columns for the next.", flip: "Like a data relay race. Each API passes the baton to the next." },
  { icon: "⏰", title: "Scheduled Runs", desc: "Cron-based automation. Daily, weekly, custom intervals. Runs while you sleep.", flip: "It's 3am. Your data pipeline is wide awake. You are not. Perfect." },
  { icon: "🪝", title: "Webhook Triggers", desc: "External systems POST to a unique URL to trigger processing instantly.", flip: "Poke it with a URL. Watch the dominoes fall." },
  { icon: "📦", title: "Template System", desc: "Save pipelines + API configs as reusable templates. Fork global templates per company.", flip: "One click and it's all there. Your past self was a genius." },
  { icon: "🏢", title: "Multi-Tenant Companies", desc: "Full data isolation. Super admin, company admin, and user roles with complete separation.", flip: "Your data is your data. We mean that in the most legally serious way." },
  { icon: "📈", title: "Analytics Dashboard", desc: "Execution trends, success rates, API latency, records processed over time.", flip: "Numbers that actually feel like an achievement." },
  { icon: "🔬", title: "Data Profiler", desc: "Column stats: null %, unique count, min/max/avg, top values before you run anything.", flip: "Inspect before you execute. Like looking both ways before crossing." },
  { icon: "✅", title: "Validation Rules", desc: "Pre-execution quality gates. Email format, ranges, required fields — warn or block.", flip: "We stop the bad rows at the door. No ID? No entry." },
  { icon: "📐", title: "OpenAPI Import", desc: "Paste a Swagger URL and watch API definitions appear. Payload with real examples.", flip: "30 API definitions in 10 seconds. Magic? Engineering? Both." },
  { icon: "🔑", title: "Variables Store", desc: "Global constants for base URLs and tokens. Reference as {{$VAR:KEY}} everywhere.", flip: "DRY principle, but make it no-code." },
];

function FeatureCard({ icon, title, desc, flip, color }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className="cursor-pointer"
      style={{ perspective: "800px", height: "160px" }}
      onClick={() => setFlipped(f => !f)}
    >
      <div
        style={{
          transition: "transform 0.6s cubic-bezier(.4,2,.6,1)",
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          position: "relative", height: "100%",
        }}
      >
        {/* Front */}
        <div className="absolute inset-0 rounded-xl border border-gray-200 bg-white p-4 flex flex-col"
          style={{ backfaceVisibility: "hidden" }}>
          <div className="text-3xl mb-2">{icon}</div>
          <h3 className="font-bold text-gray-900 text-sm mb-1">{title}</h3>
          <p className="text-xs text-gray-500 leading-relaxed flex-1">{desc}</p>
          <p className="text-[10px] text-gray-300 mt-2">click to flip ↩</p>
        </div>
        {/* Back */}
        <div
          className="absolute inset-0 rounded-xl flex items-center justify-center p-5 text-white text-sm font-semibold text-center"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
        >
          <div>
            <div className="text-2xl mb-3">😄</div>
            {flip}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pain points ───────────────────────────────────────────────────
const DOMAINS = [
  {
    name: "Sales & CRM",
    icon: "💼",
    pain: "Sales teams export CRM data to spreadsheets, manually enrich with company info from 3 different APIs, reformat dates for upload, then copy-paste results back — eating 3 hours per rep per week.",
    solution: "Upload your CRM export → chain lookup table for territory mapping → call company-data API → call contact-enrichment API → call your CRM import API. The whole sequence runs unattended. 3 hours → 4 minutes.",
  },
  {
    name: "E-commerce",
    icon: "🛒",
    pain: "Order files come in via email from wholesale partners as .xlsx. Someone has to manually parse them, look up internal SKUs, convert prices, call the ERP API, and email back a confirmation. This happens 30+ times a day.",
    solution: "IMAP monitors the partner mailbox. Attachment arrives → SKU lookup table maps partner codes to internal codes → price conversion transforms → ERP API call → confirmation auto-emailed back. Zero human touch.",
  },
  {
    name: "Finance & Accounting",
    icon: "💰",
    pain: "Monthly reconciliation requires downloading bank statements, cross-referencing 5-digit cost center codes against a master list, formatting amounts in 3 different regional formats, and calling the accounting API to post entries. Deadlines create panic.",
    solution: "Upload the bank export → range lookup for cost center bands → date format transform → currency locale formatting → accounting API chain. Add a scheduled run on the 1st of each month. Reconciliation runs itself.",
  },
  {
    name: "Healthcare",
    icon: "🏥",
    pain: "Patient billing files require ICD code lookups, insurance tier categorization by coverage band, date-of-service reformatting, and submission to separate billing APIs by payer type. One wrong code means rejected claims.",
    solution: "ICD code lookup table with wildcard fallback → range lookup for insurance tier → date format step → conditional API routing (Blue Cross API for tier 1, Medicare API for tier 2). Validation rules block malformed codes before submission.",
  },
  {
    name: "HR & Recruiting",
    icon: "👥",
    pain: "Onboarding 20 new hires involves creating accounts in 6 different systems (SSO, Slack, Jira, HR system, payroll, access control) from a single spreadsheet. IT spends 45 minutes per person clicking through portals.",
    solution: "New hire spreadsheet → transform (format employee IDs, split full names) → execute sequence: SSO API → Slack API → Jira API → HR system → payroll → access control. All 6 accounts created in order, automatically.",
  },
  {
    name: "Marketing",
    icon: "📣",
    pain: "Campaign performance data lives in 4 different ad platforms. Each exports a different CSV format with different date formats, metric names, and currency symbols. Manually normalizing and uploading to the BI tool takes half a day.",
    solution: "Upload each platform's CSV → normalize column names → convert date formats → parse currency → call BI tool API. Save as a template per platform. Weekly scheduled runs pull and normalize each source automatically.",
  },
  {
    name: "Operations & Logistics",
    icon: "🚚",
    pain: "Shipment tracking requires matching waybill numbers from carrier CSV exports against order IDs in the internal system, updating order statuses, and notifying customers via a messaging API. Missing matches cause customer complaints.",
    solution: "Carrier CSV → waybill lookup table matches carrier codes to internal order IDs → status mapping (shipped/in-transit/delivered) → order update API → conditional: if delivered, trigger notification API. Wildcard handles unknown carriers.",
  },
  {
    name: "Real Estate",
    icon: "🏠",
    pain: "Property listing uploads require geocoding, price per sqft calculations, zoning code lookups, agent assignment by postcode range, and submission to 3 listing portals. A junior admin spends 2 days each week on this.",
    solution: "Property CSV → geocoding API → calculated field for price/sqft → postcode range lookup for agent → submit to Portal A, B, C in sequence. Validation rules ensure no listing goes live without a price or address.",
  },
  {
    name: "Legal & Compliance",
    icon: "⚖️",
    pain: "Quarterly KYC reviews require pulling client data from a spreadsheet, calling a sanctions-screening API, a credit-bureau API, and a PEP database — then writing results back against each record and filing with the compliance system. Manual, error-prone, auditable.",
    solution: "Client list CSV → validation rules (required fields: DOB, ID number) → execute sequence: sanctions API → credit API → PEP API → compliance filing API. All resp_ fields written back. Export XLSX as the audit trail. Full chain automated.",
  },
  {
    name: "Freelancing",
    icon: "🧑‍💻",
    pain: "Freelancers juggle multiple clients, each with their own CRM, invoicing system, and project tool. Updating timesheets, generating invoices, pushing project updates — all from the same weekly CSV — means 3+ hours of repetitive portal work every Friday.",
    solution: "Weekly timesheet CSV → client code lookup → calculated fields (hours × rate) → invoice API for each client → project management API to mark tasks complete → send summary email. Friday automation runs at 5pm. You're already at the pub.",
  },
];

// ── Animated Flow ─────────────────────────────────────────────────
const FLOW_STEPS = [
  { id: "email", icon: "📧", label: "Email Arrives", desc: "An email lands in the monitored inbox with an Excel attachment." },
  { id: "extract", icon: "📂", label: "File Extracted", desc: "The attachment is parsed — headers detected, columns typed, rows staged." },
  { id: "transform", icon: "⚙️", label: "Data Transformed", desc: "The pipeline runs: lookups, date formats, calculated fields, validations." },
  { id: "api", icon: "🌐", label: "API Called", desc: "Each row calls the configured REST API. Rate limits handled. Results captured." },
  { id: "respond", icon: "✅", label: "Results Sent Back", desc: "The enriched data is exported as XLSX and emailed back to the sender automatically." },
];

function FlowAnimation({ color }) {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setActive(a => (a + 1) % FLOW_STEPS.length), 2500);
    return () => clearInterval(t);
  }, [auto]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Step pills */}
      <div className="flex items-center justify-between mb-10 overflow-x-auto pb-2">
        {FLOW_STEPS.map((step, i) => (
          <React.Fragment key={step.id}>
            <button
              className="flex flex-col items-center gap-2 flex-shrink-0 group"
              onClick={() => { setActive(i); setAuto(false); }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-2xl transition-all duration-500 border-2"
                style={{
                  background: active === i ? color : "#F8F9FA",
                  borderColor: active >= i ? color : "#E5E7EB",
                  transform: active === i ? "scale(1.15)" : "scale(1)",
                  boxShadow: active === i ? `0 0 0 6px ${color}22` : "none",
                }}
              >
                {step.icon}
              </div>
              <span className="text-xs font-semibold text-center whitespace-nowrap" style={{ color: active === i ? color : "#9CA3AF" }}>
                {step.label}
              </span>
            </button>
            {i < FLOW_STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 transition-all duration-500 flex-shrink-0"
                style={{ background: active > i ? color : "#E5E7EB", minWidth: "24px" }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Active step detail */}
      <div
        key={active}
        className="bg-white border-2 rounded-2xl p-8 text-center transition-all"
        style={{ borderColor: `${color}40` }}
      >
        <div className="text-6xl mb-4 animate-bounce">{FLOW_STEPS[active].icon}</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{FLOW_STEPS[active].label}</h3>
        <p className="text-gray-500 max-w-md mx-auto">{FLOW_STEPS[active].desc}</p>
        <div className="mt-4 flex justify-center gap-2">
          {FLOW_STEPS.map((_, i) => (
            <div key={i} className="w-2 h-2 rounded-full transition-all" style={{ background: i === active ? color : "#E5E7EB" }} />
          ))}
        </div>
        <button
          className="mt-4 text-xs px-3 py-1 rounded-full border transition-colors"
          style={{ borderColor: color, color: auto ? "white" : color, background: auto ? color : "transparent" }}
          onClick={() => setAuto(a => !a)}
        >
          {auto ? "⏸ Pause" : "▶ Auto-play"}
        </button>
      </div>
    </div>
  );
}

// ── Scroll reveal hook ────────────────────────────────────────────
function useReveal() {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

// ── Main landing page ─────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeDomain, setActiveDomain] = useState(0);
  const [plans, setPlans] = useState([]);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [contactModal, setContactModal] = useState(null); // plan object or null
  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [contactSending, setContactSending] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // plan_id being checked out
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" | "yearly"
  const c = theme.primary;
  const cd = theme.dark;

  // Yearly = 10 × monthly (2 months free = ~17% off)
  const displayPrice = (plan) => {
    if (plan.is_free) return null;
    if (billingPeriod === "yearly") return (plan.price_monthly * 10).toFixed(0);
    return plan.price_monthly.toFixed(0);
  };

  useEffect(() => {
    client.get("/plans/").then(r => setPlans(r.data)).catch(() => {});
    client.get("/payments/config").then(r => {
      setStripeEnabled(r.data.stripe_enabled || false);
    }).catch(() => {});
  }, []);

  const handlePlanClick = async (plan) => {
    if (plan.is_free) { navigate("/login"); return; }
    if (stripeEnabled) {
      setCheckoutLoading(plan._id);
      try {
        const res = await client.post("/payments/checkout", {
          plan_id: plan._id,
          origin_url: window.location.origin,
        });
        window.location.href = res.data.url;
      } catch (e) {
        setCheckoutLoading(null);
        alert("Payment error. Please try again.");
      }
      return;
    }
    // Stripe OFF — show contact modal
    setContactModal(plan);
    setContactForm({ name: "", email: "", company: "", phone: "", message: "" });
    setContactDone(false);
  };

  const submitContact = async () => {
    if (!contactForm.name || !contactForm.email) return;
    setContactSending(true);
    try {
      await client.post("/payments/contact", {
        ...contactForm,
        plan_name: contactModal.name,
        plan_id: contactModal._id,
      });
      setContactDone(true);
    } catch { /* ignore */ }
    finally { setContactSending(false); }
  };

  // Inject Bricolage Grotesque
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap";
    document.head.appendChild(link);
  }, []);

  const BG = "font-['Bricolage_Grotesque',sans-serif]";

  return (
    <div className={`${BG} overflow-x-hidden bg-[#F8F9FA]`}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-black" style={{ background: c }}>X</div>
            <span className="font-bold text-gray-900">{theme.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {plans.length > 0 && <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>}
            <button onClick={() => navigate("/login")} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Sign in</button>
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
              style={{ background: `linear-gradient(135deg, ${c}, ${cd})` }}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-14">
        <ParticlesBg color={c} />
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8 border"
            style={{ background: `${c}12`, borderColor: `${c}30`, color: c }}>
            <span className="w-1.5 h-1.5 rounded-full animate-ping" style={{ background: c }} />
            Now open for self-hosting
          </div>
          <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-gray-900 mb-6 leading-[1.05]">
            Excel files,<br />
            <span style={{ background: `linear-gradient(135deg, ${c} 0%, #7C3AED 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              meet the API world.
            </span>
          </h1>
          <div className="text-xl sm:text-2xl text-gray-500 min-h-[2em] mb-10">
            <TypeWriter color={c} />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-4 rounded-xl text-lg font-bold text-white transition-all hover:opacity-90 hover:scale-105 hover:shadow-xl"
              style={{ background: `linear-gradient(135deg, ${c}, ${cd})` }}
            >
              Start for free →
            </button>
            <a href="#flow"
              className="px-8 py-4 rounded-xl text-lg font-semibold border-2 transition-all hover:scale-105"
              style={{ borderColor: c, color: c }}>
              See how it works
            </a>
          </div>
          <p className="mt-6 text-sm text-gray-400">No credit card. No cloud dependency. Just your server.</p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-400">scroll</span>
          <div className="w-5 h-8 rounded-full border-2 border-gray-300 flex items-start justify-center p-1">
            <div className="w-1 h-2 rounded-full bg-gray-400" style={{ animation: "bounce 1.5s infinite" }} />
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────── */}
      <section className="py-16 px-6" style={{ background: `linear-gradient(135deg, ${c} 0%, ${cd} 100%)` }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
          {[
            ["500K+", "Rows per file"],
            ["50+", "Transform operations"],
            ["∞", "API calls possible"],
            ["0", "Lines of code required"],
          ].map(([val, label]) => (
            <Reveal key={label}>
              <div className="text-4xl font-black mb-1">{val}</div>
              <div className="text-white/70 text-sm uppercase tracking-widest">{label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: c }}>Everything you need</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">A platform, not a point tool.</h2>
              <p className="text-gray-500 max-w-xl mx-auto">Click any card to see what we really think about it.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 40}>
                <FeatureCard {...f} color={c} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── ANIMATED FLOW ─────────────────────────────────────── */}
      <section id="flow" className="py-24 px-6" style={{ background: `${c}08` }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: c }}>The magic pipeline</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">From email to result in 5 steps.</h2>
              <p className="text-gray-500 max-w-xl mx-auto">No dashboards to check. No portals to log into. Just send an email and check your inbox in a few minutes.</p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <FlowAnimation color={c} />
          </Reveal>

          {/* Quick process diagram */}
          <Reveal delay={300}>
            <div className="mt-16 bg-white rounded-2xl border border-gray-200 p-8 max-w-4xl mx-auto">
              <h3 className="text-lg font-bold text-center text-gray-900 mb-8">What happens under the hood</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { step: "1", color: c, title: "You upload or email", body: "Drop a CSV, upload via the browser, email an attachment, POST via webhook, or let the scheduler run automatically." },
                  { step: "2", color: "#7C3AED", title: "The platform transforms", body: "Lookup tables enrich rows, calculated fields derive new values, date formats normalize, type conversions clean data." },
                  { step: "3", color: "#059669", title: "APIs are called", body: "Each row becomes an API call. Responses are captured and written back. The enriched file is delivered automatically." },
                ].map(s => (
                  <div key={s.step} className="text-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-black mx-auto mb-3" style={{ background: s.color }}>{s.step}</div>
                    <h4 className="font-bold text-gray-900 mb-2">{s.title}</h4>
                    <p className="text-sm text-gray-500">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── PAIN POINTS ──────────────────────────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: c }}>Built for your world</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Your industry. Your painpoints. Solved.</h2>
              <p className="text-gray-500">Pick your domain and see exactly what changes.</p>
            </div>
          </Reveal>
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: domain tabs */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="space-y-1">
                {DOMAINS.map((d, i) => (
                  <button
                    key={d.name}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-semibold transition-all"
                    style={{
                      background: activeDomain === i ? c : "transparent",
                      color: activeDomain === i ? "white" : "#4B5563",
                    }}
                    onClick={() => setActiveDomain(i)}
                  >
                    <span className="text-lg">{d.icon}</span>
                    {d.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: detail panel */}
            <div className="flex-1" key={activeDomain}>
              <div className="bg-[#F8F9FA] rounded-2xl p-8 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">{DOMAINS[activeDomain].icon}</span>
                  <h3 className="text-2xl font-black text-gray-900">{DOMAINS[activeDomain].name}</h3>
                </div>
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 text-xs font-bold flex-shrink-0">!</div>
                    <span className="text-xs font-bold uppercase tracking-widest text-red-500">The Painpoint</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed bg-white rounded-xl p-4 border border-red-100">
                    {DOMAINS[activeDomain].pain}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: c }}>✓</div>
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: c }}>With ExcelToAPI</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed bg-white rounded-xl p-4 border" style={{ borderColor: `${c}30` }}>
                    {DOMAINS[activeDomain].solution}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────── */}
      {plans.length > 0 && (
        <section id="pricing" className="py-24 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <div className="text-center mb-10">
                <span className="text-sm font-bold uppercase tracking-widest" style={{ color: c }}>Simple, transparent pricing</span>
                <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Choose your plan.</h2>
                <p className="text-gray-500 max-w-xl mx-auto">Start free, upgrade when you're ready. No hidden fees. Cancel anytime.</p>
              </div>
            </Reveal>

            {/* Billing toggle */}
            <Reveal>
              <div className="flex items-center justify-center gap-3 mb-12">
                <button
                  onClick={() => setBillingPeriod("monthly")}
                  data-testid="billing-monthly-btn"
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${billingPeriod === "monthly" ? "text-white shadow-md" : "text-gray-500 hover:text-gray-700"}`}
                  style={billingPeriod === "monthly" ? { background: `linear-gradient(135deg, ${c}, ${cd})` } : {}}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod("yearly")}
                  data-testid="billing-yearly-btn"
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${billingPeriod === "yearly" ? "text-white shadow-md" : "text-gray-500 hover:text-gray-700"}`}
                  style={billingPeriod === "yearly" ? { background: `linear-gradient(135deg, ${c}, ${cd})` } : {}}
                >
                  Yearly
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full transition-colors ${billingPeriod === "yearly" ? "bg-white/20 text-white" : "text-white"}`}
                    style={billingPeriod !== "yearly" ? { background: c } : {}}>
                    Save 17%
                  </span>
                </button>
              </div>
            </Reveal>

            <div className={`grid grid-cols-1 md:grid-cols-${plans.length === 1 ? "1" : plans.length === 2 ? "2" : "3"} gap-6 items-stretch`}>
              {plans.map((plan, i) => (
                <Reveal key={plan._id} delay={i * 80}>
                  <div className={`relative flex flex-col h-full rounded-2xl border-2 p-8 transition-all hover:shadow-xl ${plan.is_popular ? "shadow-lg" : "hover:-translate-y-1"}`}
                    style={{ borderColor: plan.is_popular ? c : "#e5e7eb" }}>
                    {plan.is_popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-xs font-bold" style={{ background: c }}>
                        Most Popular
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="text-xl font-black text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                    </div>
                    <div className="mb-6">
                      {plan.is_free ? (
                        <span className="text-5xl font-black" style={{ color: c }}>Free</span>
                      ) : (
                        <div>
                          <div className="flex items-end gap-1">
                            <span className="text-5xl font-black text-gray-900">${displayPrice(plan)}</span>
                            <span className="text-gray-400 text-sm mb-2">/{billingPeriod === "yearly" ? "year" : "month"}</span>
                          </div>
                          {billingPeriod === "yearly" && (
                            <p className="text-xs mt-1" style={{ color: c }}>
                              Billed annually · ${plan.price_monthly}/mo equivalent
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <ul className="space-y-2.5 mb-8 flex-1">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-white text-[10px]" style={{ background: c }}>✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanClick(plan)}
                      disabled={checkoutLoading === plan._id}
                      data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90 hover:scale-105 disabled:opacity-60"
                      style={plan.is_popular
                        ? { background: `linear-gradient(135deg, ${c}, ${cd})`, color: "white" }
                        : { border: `2px solid ${c}`, color: c, background: "transparent" }
                      }
                    >
                      {checkoutLoading === plan._id ? "Redirecting..." : plan.cta_label}
                    </button>
                    {!plan.is_free && !stripeEnabled && (
                      <p className="text-xs text-gray-400 text-center mt-2">Admin will reach out with payment details</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Modal (when Stripe OFF) */}
      {contactModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setContactModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            {!contactDone ? (
              <>
                <h2 className="text-2xl font-black text-gray-900 mb-1">Interested in {contactModal.name}?</h2>
                <p className="text-gray-500 text-sm mb-6">Leave your details and our admin will reach out shortly with a payment link. Your account will be activated upon payment completion.</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Name *</label>
                    <input className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2" style={{ "--tw-ring-color": c }} value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Email *</label>
                    <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2" value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} placeholder="you@company.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Company</label>
                    <input className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2" value={contactForm.company} onChange={e => setContactForm(f => ({ ...f, company: e.target.value }))} placeholder="Company name" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Phone</label>
                    <input className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2" value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555 000 0000" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Message (optional)</label>
                    <textarea rows={2} className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 resize-none" value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} placeholder="Any questions or requirements..." />
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <button onClick={() => setContactModal(null)} className="flex-1 py-2.5 rounded-xl border text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={submitContact}
                    disabled={contactSending || !contactForm.name || !contactForm.email}
                    data-testid="contact-submit-btn"
                    className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${c}, ${cd})` }}
                  >
                    {contactSending ? "Sending..." : "Send Request"}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="text-5xl mb-4">✅</div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Request Received!</h2>
                <p className="text-gray-600 text-sm mb-6">Our admin will reach out to you at <strong>{contactForm.email}</strong> with a payment link. Your account will be activated once payment is complete.</p>
                <button onClick={() => setContactModal(null)} className="w-full py-3 rounded-xl text-white font-bold" style={{ background: `linear-gradient(135deg, ${c}, ${cd})` }}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PRINCIPLES & ETHICS ──────────────────────────────── */}
      <section className="py-24 px-6" style={{ background: `${c}06` }}>
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: c }}>Built with intention</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Principles we don't compromise on.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: "🏠", title: "Your infrastructure. Your data.", body: "ExcelToAPI runs entirely on your own server. Your data never touches our systems, never crosses our network, never ends up in a shared cloud. What goes in stays in." },
              { icon: "🔒", title: "Intranet-first security.", body: "Deploy behind a firewall and it never needs to be internet-facing. SMTP, IMAP, API calls go outbound only. No inbound ports required beyond the web UI." },
              { icon: "🔑", title: "Encrypted at rest.", body: "API credentials, SMTP passwords, and IMAP passwords are AES-256 encrypted before touching the database. Keys are generated locally and live only on your server." },
              { icon: "🧱", title: "No vendor lock-in.", body: "This is open architecture. MongoDB, your own SMTP server, your own file system. Switch any component. Take your data. We don't hold any of it." },
              { icon: "🌍", title: "GDPR-friendly by design.", body: "Data isolation by company means each tenant's data is physically separate in the database. Audit logs track every action. Retention is your call, not ours." },
              { icon: "👁️", title: "Full audit trail.", body: "Every action — login, upload, execution, user creation — is logged with timestamp, IP, and user identity. Compliance teams can breathe easily." },
              { icon: "🏋️", title: "Runs on modest hardware.", body: "A 4-core VPS with 8GB RAM handles hundreds of concurrent executions and millions of records processed per day. No Kubernetes required." },
              { icon: "🤝", title: "Honest about limitations.", body: "This tool automates file-to-API workflows. It's not an ETL platform, not a data warehouse, not an iPaaS. It does one thing extremely well." },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 60}>
                <div className="bg-white rounded-2xl p-6 border border-gray-200 flex gap-4 hover:shadow-md transition-all">
                  <div className="text-3xl flex-shrink-0">{p.icon}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="py-24 px-6" style={{ background: `linear-gradient(135deg, ${c} 0%, ${cd} 50%, #4C1D95 100%)` }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <Reveal>
            <div className="text-5xl mb-6">👋</div>
            <h2 className="text-4xl font-black mb-4">Ready to automate your data workflows?</h2>
            <p className="text-white/80 text-lg mb-4">
              We help teams deploy ExcelToAPI on their own infrastructure. <br />Whether it's a quick VPS or a corporate server, we'll get you set up.
            </p>
            <p className="text-white/60 mb-8 text-sm">We respond to every message. Usually within a week. Sometimes faster if it's interesting.</p>
            <a
              href="mailto:hello@krri.sh?subject=ExcelToAPI%20Enquiry"
              className="inline-block px-10 py-4 rounded-xl text-lg font-bold bg-white transition-all hover:scale-105 hover:shadow-2xl"
              style={{ color: c }}
            >
              Email us at hello@krri.sh →
            </a>
            <p className="mt-6 text-white/50 text-xs">
              No sales calls. No demo requests. Just a conversation about what you need to automate.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-black" style={{ background: c }}>X</div>
          <span className="font-bold text-white">{theme.name}</span>
        </div>
        <p className="text-gray-400 text-sm">Self-hosted. Your data. Your rules.</p>
        <div className="flex justify-center gap-6 mt-4 text-xs text-gray-500">
          <button onClick={() => navigate("/login")} className="hover:text-white transition-colors">Sign in</button>
          {plans.length > 0 && <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>}
          <a href="mailto:hello@krri.sh" className="hover:text-white transition-colors">Contact</a>
        </div>
      </footer>
    </div>
  );
}
